import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import {useParams} from "react-router-dom";
import Box from "@mui/material/Box";
import Swal from "sweetalert2";
import AutoComplete from "@mui/material/Autocomplete";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";
export default function ConversationRecorder() {
    // ============================================================
    // ROUTE
    // ============================================================
    const {appt_id} = useParams();
    // ============================================================
    // CASE SHEET STATE
    // ============================================================
    const [convId, setConvId] = useState(null);
    const [caseSheet, setCaseSheet] = useState(null);
    const [loading, setLoading] = useState(false);
    // ============================================================
    // RECORDING STATE
    // ============================================================
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const stoppingRef = useRef(false);
    // ============================================================
    // SPEECH RECOGNITION STATE
    // ============================================================
    const recognitionRef = useRef(null);
    const recognitionStartingRef = useRef(false);
    const shouldContinueRef = useRef(false);
    const finalTranscriptRef = useRef("");
    const [transcript, setTranscript] = useState("");
    const [language, setLanguage] = useState("ta-IN");
    const [speechListening, setSpeechListening] = useState(false);
    // ============================================================
    // AUTHENTICATION
    // ============================================================
    const getAccessToken = () => {
        try {
            const tokens = JSON.parse(localStorage.getItem("authTokens"));
            return tokens?.access || null;
        } catch (error) {
            console.error("Unable to read auth token:", error);
            return null;
        }
    };
    const refreshAccessToken = async () => {
        try {
            const tokens = JSON.parse(localStorage.getItem("authTokens"));
            if (!tokens?.refresh) {
                return null;
            }
            const response = await fetch(baseUrl + "token/refresh/", {
                method: "POST", headers: {
                    "Content-Type": "application/json"
                }, body: JSON.stringify({
                    refresh: tokens.refresh
                })
            });
            if (!response.ok) {
                console.error("Token refresh failed:", response.status);
                return null;
            }
            const data = await response.json();
            tokens.access = data.access;
            localStorage.setItem("authTokens", JSON.stringify(tokens));
            return data.access;
        } catch (error) {
            console.error("Token refresh error:", error);
            return null;
        }
    };
    const authFetch = async (url, options = {}, retry = true) => {
        let accessToken = getAccessToken();
        options.headers = {
            ...(options.headers || {}), Authorization: `Bearer ${accessToken}`
        };
        let response = await fetch(url, options);
        if (response.status === 401 && retry) {
            accessToken = await refreshAccessToken();
            if (!accessToken) {
                return response;
            }
            options.headers.Authorization = `Bearer ${accessToken}`;
            response = await fetch(url, options);
        }
        return response;
    };
    // ============================================================
    // LANGUAGE DETECTION
    // ============================================================
    const detectSpeechLanguage = (text) => {
        if (!text || !text.trim()) {
            return "unknown";
        }
        const tamilChars = text.match(/[\u0B80-\u0BFF]/g) || [];
        const englishChars = text.match(/[A-Za-z]/g) || [];
        if (tamilChars.length > englishChars.length) {
            return "ta-IN";
        }
        if (englishChars.length > 0) {
            return "en-IN";
        }
        return "unknown";
    };
    // ============================================================
    // START AUDIO RECORDING
    // ============================================================
    const startRecording = async () => {
        try {
            // ----------------------------------------------------
            // Reset everything from previous consultation
            // ----------------------------------------------------
            audioChunksRef.current = [];
            finalTranscriptRef.current = "";
            setTranscript("");
            setCaseSheet(null);
            setConvId(null);
            setLoading(false);
            stoppingRef.current = false;
            shouldContinueRef.current = true;
            // ----------------------------------------------------
            // Get microphone
            // ----------------------------------------------------
            const stream = await navigator.mediaDevices
                .getUserMedia({
                    audio: true
                });
            // ----------------------------------------------------
            // Create MediaRecorder
            // ----------------------------------------------------
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            // ----------------------------------------------------
            // Collect audio
            // ----------------------------------------------------
            mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            // ----------------------------------------------------
            // Start recording
            // ----------------------------------------------------
            mediaRecorder.start();
            setIsRecording(true);
            console.log("======================================");
            console.log("AUDIO RECORDING STARTED");
            console.log("Language:", language);
            console.log("======================================");
            // ----------------------------------------------------
            // Start browser speech recognition
            // ----------------------------------------------------
            startSpeechRecognition();
        } catch (error) {
            console.error("Microphone error:", error);
            setIsRecording(false);
            shouldContinueRef.current = false;
            Swal.fire("Microphone Error", "Unable to access the microphone. Please allow microphone permission.", "error");
        }
    };
    // ============================================================
    // START BROWSER SPEECH RECOGNITION
    // ============================================================
    const startSpeechRecognition = () => {
        const recognition = recognitionRef.current;
        if (!recognition) {
            console.error("Speech recognition is not available.");
            return;
        }
        if (recognitionStartingRef.current || speechListening) {
            return;
        }
        try {
            recognition.lang = language;
            recognitionStartingRef.current = true;
            recognition.start();
            console.log("Starting browser speech recognition...");
        } catch (error) {
            recognitionStartingRef.current = false;
            console.warn("Speech recognition start:", error);
        }
    };
    // ============================================================
    // STOP AUDIO RECORDING
    // ============================================================
    const stopRecording = () => {
        return new Promise((resolve) => {
            const mediaRecorder = mediaRecorderRef.current;
            if (!mediaRecorder) {
                setIsRecording(false);
                resolve(null);
                return;
            }
            // ----------------------------------------------------
            // Stop speech recognition first
            // ----------------------------------------------------
            shouldContinueRef.current = false;
            stoppingRef.current = true;
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch (error) {
                    console.log("Recognition already stopped.");
                }
            }
            // ----------------------------------------------------
            // MediaRecorder stop callback
            // ----------------------------------------------------
            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, {
                    type: mediaRecorder.mimeType || "audio/webm"
                });
                // ------------------------------------------------
                // Release microphone
                // ------------------------------------------------
                mediaRecorder.stream
                    .getTracks()
                    .forEach(track => track.stop());
                mediaRecorderRef.current = null;
                setIsRecording(false);
                setSpeechListening(false);
                console.log("======================================");
                console.log("AUDIO RECORDING STOPPED");
                console.log("Audio size:", audioBlob.size);
                console.log("======================================");
                resolve(audioBlob);
            };
            // ----------------------------------------------------
            // Stop recorder
            // ----------------------------------------------------
            if (mediaRecorder.state !== "inactive") {
                mediaRecorder.stop();
            } else {
                resolve(null);
            }
        });
    };
    // ============================================================
    // CREATE SPEECH RECOGNITION INSTANCE
    //
    // IMPORTANT:
    // ONLY ONE useEffect.
    // Your previous code had this TWICE.
    // ============================================================
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.error("Browser Speech Recognition is not supported.");
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.lang = language;
        // ========================================================
        // ON START
        // ========================================================
        recognition.onstart = () => {
            console.log("Browser Speech Recognition STARTED");
            recognitionStartingRef.current = false;
            setSpeechListening(true);
        };
        // ========================================================
        // ON RESULT
        // ========================================================
        recognition.onresult = (event) => {
            let interimTranscript = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                const text = result[0]?.transcript || "";
                if (!text.trim()) {
                    continue;
                }
                if (result.isFinal) {
                    finalTranscriptRef.current += text.trim() + " ";
                } else {
                    interimTranscript += text;
                }
            }
            const fullText = (finalTranscriptRef.current + interimTranscript).trim();
            setTranscript(fullText);
            console.log("BROWSER TRANSCRIPT:", fullText);
        };
        // ========================================================
        // ON ERROR
        // ========================================================
        recognition.onerror = (event) => {
            console.error("Speech recognition error:", event.error);
            recognitionStartingRef.current = false;
            if (event.error === "not-allowed") {
                shouldContinueRef.current = false;
                Swal.fire("Microphone Permission", "Please allow microphone access in your browser.", "warning");
                return;
            }
            if (event.error === "audio-capture") {
                Swal.fire("Microphone Error", "Microphone could not be accessed.", "error");
                return;
            }
            if (event.error === "network") {
                console.warn("Browser speech recognition network error.");
            }
        };
        // ========================================================
        // ON END
        // ========================================================
        recognition.onend = () => {
            console.log("Browser Speech Recognition ENDED");
            setSpeechListening(false);
            recognitionStartingRef.current = false;
            // ----------------------------------------------------
            // Chrome frequently stops recognition automatically.
            // Restart while consultation is still running.
            // ----------------------------------------------------
            if (shouldContinueRef.current && !stoppingRef.current && isRecording) {
                setTimeout(() => {
                    if (!shouldContinueRef.current || stoppingRef.current) {
                        return;
                    }
                    try {
                        recognition.lang = language;
                        recognitionStartingRef.current = true;
                        recognition.start();
                    } catch (error) {
                        recognitionStartingRef.current = false;
                        console.log("Recognition restart:", error);
                    }
                }, 300);
            }
        };
        recognitionRef.current = recognition;
        // ========================================================
        // CLEANUP
        // ========================================================
        return () => {
            shouldContinueRef.current = false;
            stoppingRef.current = true;
            try {
                recognition.stop();
            } catch (error) {
                // Already stopped
            }
            recognitionRef.current = null;
        };
    }, [language]);
    // ============================================================
    // SEND AUDIO TO DJANGO
    // ============================================================
    const sendConversation = async () => {
        try {
            console.log("======================================");
            console.log("FINISHING CONSULTATION");
            console.log("======================================");
            setLoading(true);
            // ----------------------------------------------------
            // Stop recording and get Blob
            // ----------------------------------------------------
            const audioBlob = await stopRecording();
            if (!audioBlob || audioBlob.size === 0) {
                console.error("No audio recorded.");
                setLoading(false);
                Swal.fire("No Recording", "No audio was recorded.", "warning");
                return;
            }
            // ----------------------------------------------------
            // Prepare FormData
            // ----------------------------------------------------
            const formData = new FormData();
            formData.append("audio", audioBlob, "doctor_speech.webm");
            formData.append("language", language || "ta-IN");
            formData.append("patient_name", patientName || "");
            // ----------------------------------------------------
            // Appointment information
            // ----------------------------------------------------
            if (appt_id) {
                formData.append("appt_id", appt_id);
            }
            // ----------------------------------------------------
            // Location
            // ----------------------------------------------------
            if (userLocation) {
                formData.append("location", userLocation);
            }
            console.log("Sending audio to Django...");
            // ----------------------------------------------------
            // POST
            // ----------------------------------------------------
            const response = await axios.post(baseUrl + "conversations/", formData, {
                headers: {
                    Authorization: `Bearer ${getAccessToken()}`, "Content-Type": "multipart/form-data"
                }
            });
            console.log("======================================");
            console.log("CONVERSATION RESPONSE");
            console.log(response.data);
            console.log("======================================");
            // ----------------------------------------------------
            // VERY IMPORTANT
            //
            // Your backend has now created the conversation.
            // Store its ID.
            // ----------------------------------------------------
            const newConversationId = response.data?.id || response.data?.conversation_id || response.data?.conv_id;
            if (!newConversationId) {
                console.error("Conversation ID missing from backend response:", response.data);
                setLoading(false);
                Swal.fire("Processing Error", "Conversation was uploaded, but no conversation ID was returned.", "error");
                return;
            }
            setConvId(newConversationId);
            console.log("Conversation ID:", newConversationId);
            // ----------------------------------------------------
            // NOW POLL FOR CASE SHEET
            // ----------------------------------------------------
            await pollForResult(newConversationId);
        } catch (error) {
            console.error("======================================");
            console.error("CONVERSATION UPLOAD FAILED");
            console.error(error);
            console.error("======================================");
            setLoading(false);
            Swal.fire("Error", "Unable to process the consultation.", "error");
        }
    };
    // ============================================================
    // POLL CASE SHEET
    // ============================================================
    const pollForResult = async (id) => {
        const maxAttempts = 40;
        for (let i = 0; i < maxAttempts; i++) {
            try {
                console.log(`Polling case sheet ${i + 1}/${maxAttempts}`);
                const response = await authFetch(`${baseUrl}conversations/${id}/`);
                if (!response.ok) {
                    console.error("Polling failed:", response.status);
                } else {
                    const data = await response.json();
                    console.log("CASE SHEET POLL RESPONSE:", data);
                    // ------------------------------------------------
                    // Case sheet available
                    // ------------------------------------------------
                    if (data.case_sheet) {
                        console.log("======================================");
                        console.log("CASE SHEET RECEIVED");
                        console.log(data.case_sheet);
                        console.log("======================================");
                        setCaseSheet(data.case_sheet);
                        setLoading(false);
                        return;
                    }
                }
            } catch (error) {
                console.error("Polling error:", error);
            }
            // ----------------------------------------------------
            // Wait before next poll
            // ----------------------------------------------------
            await new Promise(resolve => setTimeout(resolve, 1200));
        }
        // --------------------------------------------------------
        // Timeout
        // --------------------------------------------------------
        setLoading(false);
        Swal.fire("Processing", "The case sheet is still being processed. Please refresh after a moment.", "info");
    };
    // ============================================================
    // PATIENT NAME
    // ============================================================
    const patientName = "Gopi";
    // ============================================================
    // ============================================================
// PART 2 + PART 3
// CASE SHEET + LAB + RADIOLOGY + MEDICINE
// ============================================================
// ============================================================
// MEDICINE / PHARMACY STATE
// ============================================================
    const [drugitems, setDrugItems] = useState([]);
    const [drugrows, setDrugRows] = useState([
        {
            id: "",
            product: null,
            dose: "",
            freq: "",
            remarks: ""
        }
    ]);
// ============================================================
// LAB STATE
// ============================================================
    const [labitems, setLabItems] = useState([]);
    const [labrows, setLabRows] = useState([
        {
            id: "",
            subcat: null,
            amount: ""
        }
    ]);
    const [labtotal, setLabTotal] = useState(0);
    const [labsubtotal, setLabSubtotal] = useState(0);
    const [labdisPer, setLabDisPer] = useState(0);
    const [labdisAmount, setLabDisAmount] = useState(0);
// ============================================================
// RADIOLOGY / INVESTIGATION STATE
// ============================================================
    const [invitems, setInvItems] = useState([]);
    const [invrows, setInvRows] = useState([
        {
            id: "",
            components: null,
            amount: ""
        }
    ]);
    const [invtotal, setInvTotal] = useState(0);
    const [invsubtotal, setInvSubtotal] = useState(0);
    const [invdisPer, setInvDisPer] = useState(0);
    const [invdisAmount, setInvDisAmount] = useState(0);
// ============================================================
// FORM DATA
// ============================================================
    const [labData, setLabData] = useState({
        appt_id: "",
        pat: "",
        cons: "",
        lab_id: "",
        ltest_id: "",
        lab_test: "",
        lab_amount: "",
        lab_tot: "",
        lab_disc: "",
        lab_status: "",
        addedby: "",
        ip_add: ""
    });
    const [invData, setInvData] = useState({
        appt_id: "",
        pat: "",
        cons: "",
        inv_id: "",
        itest_id: "",
        inv_test: "",
        inv_amount: "",
        inv_tot: "",
        inv_disc: "",
        inv_status: "",
        addedby: "",
        ip_add: ""
    });
// ============================================================
// AUTOCOMPLETE REFS
// ============================================================
    const inputLabRefs = useRef([]);
    const inputInvRefs = useRef([]);
    const inputDrugRefs = useRef([]);
// ============================================================
// NORMALIZATION
// ============================================================
    const normalizeText = (value = "") => {
        return String(value)
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();
    };
    const normalizeMedicalName = (value = "") => {
        return normalizeText(value)
            .replace(/\([^)]*\)/g, " ")
            .replace(
                /\b(tab|tablet|tablets|cap|capsule|capsules|inj|injection|medicine)\b/gi,
                " "
            )
            .replace(/\s+/g, " ")
            .trim();
    };
    const normalizeMedicineName = (value = "") => {
        return normalizeMedicalName(value);
    };
    const normalizeRadiologyName = (value = "") => {
        return normalizeText(value)
            .replace(/\bu\.?\s*s\.?\s*g\.?\b/gi, "usg")
            .replace(/\bx[-\s]?ray\b/gi, "x ray")
            .replace(/[.,/]+/g, " ")
            .replace(/\s+/g, " ")
            .trim();
    };
    const normalizeLabName = (value = "") => {
        return normalizeMedicalName(value);
    };
// ============================================================
// SAFE ARRAY PARSER
// ============================================================
    const parseCaseSheetArray = (value) => {
        if (!value) {
            return [];
        }
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === "object") {
            return [value];
        }
        if (typeof value !== "string") {
            return [];
        }
        const text = value.trim();
        if (!text) {
            return [];
        }
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) {
                return parsed;
            }
            if (parsed) {
                return [parsed];
            }
        } catch (error) {
            // continue
        }
        try {
            const parsed = JSON.parse(
                text.replace(/'/g, '"')
            );
            if (Array.isArray(parsed)) {
                return parsed;
            }
            if (parsed) {
                return [parsed];
            }
        } catch (error) {
            // continue
        }
        return text
            .split(",")
            .map(x => x.trim())
            .filter(Boolean);
    };
// ============================================================
// GET MEDICINE NAME
// ============================================================
    const getMedicineName = (medicine) => {
        if (!medicine) {
            return "";
        }
        if (typeof medicine === "string") {
            return medicine.trim();
        }
        if (medicine?.name) {
            return medicine.name;
        }
        if (medicine?.product) {
            if (typeof medicine.product === "string") {
                return medicine.product;
            }
            if (medicine.product?.product) {
                return medicine.product.product;
            }
        }
        return "";
    };
// IMPORTANT:
// Your old JSX/functions were calling getDrugName().
// Keep this alias so nothing breaks.
    const getDrugName = (drug) => {
        return getMedicineName(drug);
    };
// ============================================================
// GET LAB NAME
// ============================================================
    const getLabName = (lab) => {
        if (!lab) {
            return "";
        }
        if (typeof lab === "string") {
            return lab.trim();
        }
        return (
            lab?.name ||
            lab?.subcat ||
            lab?.test ||
            ""
        );
    };
// ============================================================
// GET RADIOLOGY NAME
// ============================================================
    const getRadiologyName = (investigation) => {
        if (!investigation) {
            return "";
        }
        if (typeof investigation === "string") {
            return investigation.trim();
        }
        if (typeof investigation?.components === "string") {
            return investigation.components;
        }
        if (typeof investigation?.components?.components === "string") {
            return investigation.components.components;
        }
        return (
            investigation?.name ||
            investigation?.test ||
            ""
        );
    };
// IMPORTANT:
// Your old code calls getInvName().
// Keep this alias.
    const getInvName = (investigation) => {
        return getRadiologyName(investigation);
    };
// ============================================================
// GET MEDICAL ITEM NAME
// ============================================================
    const getMedicalItemName = (item) => {
        if (!item) {
            return "";
        }
        if (typeof item === "string") {
            return item;
        }
        if (item?.product) {
            if (typeof item.product === "string") {
                return item.product;
            }
            if (item.product?.product) {
                return item.product.product;
            }
        }
        return (
            item?.name ||
            item?.subcat ||
            item?.components ||
            item?.test ||
            ""
        );
    };
// ============================================================
// CASE SHEET LIST HELPERS
// ============================================================
    const getCaseSheetLabs = (sheet) => {
        if (!sheet) {
            return [];
        }
        if (Array.isArray(sheet.labs)) {
            return sheet.labs;
        }
        if (Array.isArray(sheet.Labs)) {
            return sheet.Labs;
        }
        if (sheet.lab) {
            return parseCaseSheetArray(sheet.lab);
        }
        return [];
    };
    const getCaseSheetRadiology = (sheet) => {
        if (!sheet) {
            return [];
        }
        if (Array.isArray(sheet.radiology)) {
            return sheet.radiology;
        }
        if (Array.isArray(sheet.Radiology)) {
            return sheet.Radiology;
        }
        if (Array.isArray(sheet.investigations)) {
            return sheet.investigations;
        }
        if (sheet.investigation) {
            return parseCaseSheetArray(sheet.investigation);
        }
        return [];
    };
    const getCaseSheetMedicines = (sheet) => {
        if (!sheet) {
            return [];
        }
        if (Array.isArray(sheet.medicines)) {
            return sheet.medicines;
        }
        if (Array.isArray(sheet.Medicines)) {
            return sheet.Medicines;
        }
        if (sheet.medicine) {
            return parseCaseSheetArray(sheet.medicine);
        }
        if (Array.isArray(sheet.prescriptions)) {
            return sheet.prescriptions;
        }
        return [];
    };
// ============================================================
// FETCH LAB MASTER
// ============================================================
    const fetchLabData = async () => {
        try {
            console.log("Loading lab master...");
            const response = await axios.get(
                `${baseUrl}lab-search/`
            );
            const data = Array.isArray(response.data)
                ? response.data
                : [];
            console.log("Lab master count:", data.length);
            setLabItems(data);
        } catch (error) {
            console.error(
                "Lab master loading failed:",
                error
            );
        }
    };
// ============================================================
// FETCH RADIOLOGY MASTER
// ============================================================
    const fetchInvData = async () => {
        try {
            console.log("Loading radiology master...");
            const response = await axios.get(
                `${baseUrl}inv-search/`
            );
            const data = Array.isArray(response.data)
                ? response.data
                : [];
            console.log(
                "Radiology master count:",
                data.length
            );
            setInvItems(data);
        } catch (error) {
            console.error(
                "Radiology master loading failed:",
                error
            );
        }
    };
// ============================================================
// FETCH PHARMACY MASTER
// ============================================================
    const fetchPharmData = async () => {
        try {
            console.log("Loading pharmacy master...");
            const response = await axios.get(
                `${baseUrl}indent-pharm-search/?dept=Pharmacy`
            );
            const data = Array.isArray(response.data)
                ? response.data
                : [];
            console.log(
                "Medicine master count:",
                data.length
            );
            setDrugItems(data);
        } catch (error) {
            console.error(
                "Medicine master loading failed:",
                error
            );
        }
    };
// ============================================================
// LOAD MASTER DATA
// ============================================================
    useEffect(() => {
        fetchLabData();
        fetchInvData();
        fetchPharmData();
    }, []);
// ============================================================
// MAP LABS FROM CASE SHEET
// ============================================================
    useEffect(() => {
        if (!caseSheet || !labitems.length) {
            return;
        }
        const backendLabs = getCaseSheetLabs(caseSheet);
        console.log(
            "CASE SHEET LABS:",
            backendLabs
        );
        const mappedLabs = [];
        backendLabs.forEach(item => {
            const name = getMedicalItemName(item);
            if (!name) {
                return;
            }
            const requested = normalizeLabName(name);
            let match = labitems.find(
                lab =>
                    normalizeLabName(lab?.subcat) === requested
            );
            if (!match) {
                match = labitems.find(lab => {
                    const master =
                        normalizeLabName(lab?.subcat);
                    return (
                        master.includes(requested) ||
                        requested.includes(master)
                    );
                });
            }
            if (match) {
                mappedLabs.push({
                    id: match.id,
                    subcat: match.subcat,
                    amount: Number(match.amount) || 0
                });
            } else {
                console.warn(
                    "LAB NOT FOUND:",
                    name
                );
            }
        });
        const uniqueLabs = [];
        const seen = new Set();
        mappedLabs.forEach(item => {
            const key =
                normalizeLabName(item.subcat);
            if (!seen.has(key)) {
                seen.add(key);
                uniqueLabs.push(item);
            }
        });
        if (uniqueLabs.length) {
            setLabRows(uniqueLabs);
        } else {
            setLabRows([
                {
                    id: "",
                    subcat: null,
                    amount: ""
                }
            ]);
        }
    }, [caseSheet, labitems]);
// ============================================================
// MAP RADIOLOGY FROM CASE SHEET
// ============================================================
    useEffect(() => {
        if (!caseSheet || !invitems.length) {
            return;
        }
        const backendRadiology =
            getCaseSheetRadiology(caseSheet);
        console.log(
            "CASE SHEET RADIOLOGY:",
            backendRadiology
        );
        const mappedRadiology = [];
        backendRadiology.forEach(item => {
            const name =
                getMedicalItemName(item);
            if (!name) {
                return;
            }
            const requested =
                normalizeRadiologyName(name);
            let match = invitems.find(
                inv =>
                    normalizeRadiologyName(
                        inv?.components
                    ) === requested
            );
            if (!match) {
                match = invitems.find(inv => {
                    const master =
                        normalizeRadiologyName(
                            inv?.components
                        );
                    return (
                        master === requested ||
                        master.includes(requested) ||
                        requested.includes(master)
                    );
                });
            }
            if (!match && requested.includes("usg")) {
                match = invitems.find(inv => {
                    const master =
                        normalizeRadiologyName(
                            inv?.components
                        );
                    return (
                        master.includes("usg") &&
                        requested
                            .split(" ")
                            .filter(Boolean)
                            .every(word =>
                                master.includes(word)
                            )
                    );
                });
            }
            if (match) {
                mappedRadiology.push({
                    id: match.id,
                    components: match,
                    amount: Number(match.amount) || 0
                });
            } else {
                console.warn(
                    "RADIOLOGY NOT FOUND:",
                    name
                );
            }
        });
        const uniqueRadiology = [];
        const seen = new Set();
        mappedRadiology.forEach(item => {
            const key =
                normalizeRadiologyName(
                    item.components?.components
                );
            if (!seen.has(key)) {
                seen.add(key);
                uniqueRadiology.push(item);
            }
        });
        if (uniqueRadiology.length) {
            setInvRows(uniqueRadiology);
        } else {
            setInvRows([
                {
                    id: "",
                    components: null,
                    amount: ""
                }
            ]);
        }
    }, [caseSheet, invitems]);
// ============================================================
// MAP MEDICINES FROM CASE SHEET
// ============================================================
    useEffect(() => {
        if (!caseSheet || !drugitems.length) {
            return;
        }
        const backendMedicines =
            getCaseSheetMedicines(caseSheet);
        console.log(
            "CASE SHEET MEDICINES:",
            backendMedicines
        );
        const mappedMedicines = [];
        backendMedicines.forEach(item => {
            const name =
                getMedicalItemName(item);
            if (!name) {
                return;
            }
            const requested =
                normalizeMedicineName(name);
            let match = drugitems.find(drug => {
                const masterName =
                    drug?.product?.product || "";
                return (
                    normalizeMedicineName(
                        masterName
                    ) === requested
                );
            });
            if (!match) {
                match = drugitems.find(drug => {
                    const masterName =
                        drug?.product?.product || "";
                    const master =
                        normalizeMedicineName(
                            masterName
                        );
                    return (
                        master.includes(requested) ||
                        requested.includes(master)
                    );
                });
            }
            if (match) {
                mappedMedicines.push({
                    id: match.id,
                    product: match,
                    dose: "",
                    freq: "",
                    remarks: "",
                    qty: match.qty || "",
                    batch:
                        match.batch ||
                        match.product?.batch ||
                        "",
                    expiry:
                        match.expiry ||
                        match.product?.expiry ||
                        ""
                });
            } else {
                console.warn(
                    "MEDICINE NOT FOUND:",
                    name
                );
            }
        });
        const uniqueMedicines = [];
        const seen = new Set();
        mappedMedicines.forEach(item => {
            const key =
                normalizeMedicineName(
                    item.product?.product?.product
                );
            if (!seen.has(key)) {
                seen.add(key);
                uniqueMedicines.push(item);
            }
        });
        if (uniqueMedicines.length) {
            setDrugRows(uniqueMedicines);
        } else {
            setDrugRows([
                {
                    id: "",
                    product: null,
                    dose: "",
                    freq: "",
                    remarks: ""
                }
            ]);
        }
    }, [caseSheet, drugitems]);
// ============================================================
// LAB ROW HANDLERS
// ============================================================
    const handleAddRowLab = () => {
        setLabRows(prev => [
            ...prev,
            {
                id: "",
                subcat: null,
                amount: ""
            }
        ]);
    };
    const handleDeleteRowLab = index => {
        setLabRows(prev =>
            prev.filter((_, i) => i !== index)
        );
    };
// aliases for old JSX
    const handleAddRowlab = handleAddRowLab;
    const handleDeleteRowlab = handleDeleteRowLab;
// ============================================================
// RADIOLOGY ROW HANDLERS
// ============================================================
    const handleAddRowInv = () => {
        setInvRows(prev => [
            ...prev,
            {
                id: "",
                components: null,
                amount: ""
            }
        ]);
    };
    const handleDeleteRowInv = index => {
        setInvRows(prev =>
            prev.filter((_, i) => i !== index)
        );
    };
    const handleAddRowinv = handleAddRowInv;
    const handleDeleteRowinv = handleDeleteRowInv;
// ============================================================
// MEDICINE ROW HANDLERS
// ============================================================
    const handleAddRowDrug = () => {
        setDrugRows(prev => [
            ...prev,
            {
                id: "",
                product: null,
                dose: "",
                freq: "",
                remarks: ""
            }
        ]);
    };
    const handleDeleteRowDrug = index => {
        setDrugRows(prev =>
            prev.filter((_, i) => i !== index)
        );
    };
    const handleAddRowdrug = handleAddRowDrug;
    const handleDeleteRowdrug = handleDeleteRowDrug;
// ============================================================
// MEDICINE CHANGE
// ============================================================
    const handleDrugChange = (event, index) => {
        const {
            name,
            value
        } = event.target;
        setDrugRows(prev => {
            const rows = [...prev];
            rows[index] = {
                ...rows[index],
                [name]: value
            };
            return rows;
        });
    };
// Old JSX compatibility
    const handledrugChange = handleDrugChange;
// ============================================================
// LAB AMOUNT CHANGE
// ============================================================
    const handleLabAmountChange = (event, index) => {
        const value =
            parseFloat(event.target.value) || 0;
        setLabRows(prev => {
            const rows = [...prev];
            rows[index] = {
                ...rows[index],
                amount: value
            };
            return rows;
        });
    };
// ============================================================
// RADIOLOGY AMOUNT CHANGE
// ============================================================
    const handleInvAmountChange = (event, index) => {
        const value =
            parseFloat(event.target.value) || 0;
        setInvRows(prev => {
            const rows = [...prev];
            rows[index] = {
                ...rows[index],
                amount: value
            };
            return rows;
        });
    };
// ============================================================
// LAB SELECTION
// ============================================================
    const handleLabSelect = (index, option) => {
        setLabRows(prev => {
            const rows = [...prev];
            rows[index] = {
                id: option?.id || "",
                subcat: option?.subcat || "",
                amount: Number(option?.amount) || 0
            };
            return rows;
        });
    };
// ============================================================
// RADIOLOGY SELECTION
// ============================================================
    const handleRadiologySelect = (index, option) => {
        setInvRows(prev => {
            const rows = [...prev];
            rows[index] = {
                id: option?.id || "",
                components: option || null,
                amount: Number(option?.amount) || 0
            };
            return rows;
        });
    };
// ============================================================
// MEDICINE SELECTION
// ============================================================
    const handleMedicineSelect = (index, option) => {
        setDrugRows(prev => {
            const rows = [...prev];
            rows[index] = {
                id: option?.id || "",
                product: option || null,
                dose: rows[index]?.dose || "",
                freq: rows[index]?.freq || "",
                remarks: rows[index]?.remarks || ""
            };
            return rows;
        });
    };
// ============================================================
// DELETE / ADD ALIASES
// ============================================================
    const deleteLabRow = handleDeleteRowLab;
    const deleteRadiologyRow = handleDeleteRowInv;
    const deleteMedicineRow = handleDeleteRowDrug;
    const addLabRow = handleAddRowLab;
    const addRadiologyRow = handleAddRowInv;
    const addMedicineRow = handleAddRowDrug;
// ============================================================
// MEDICINE FIELD CHANGE
// ============================================================
    const handleMedicineFieldChange = (
        index,
        field,
        value
    ) => {
        setDrugRows(prev => {
            const rows = [...prev];
            rows[index] = {
                ...rows[index],
                [field]: value
            };
            return rows;
        });
    };
// ============================================================
// LAB TOTAL
// ============================================================
    useEffect(() => {
        const subtotal = labrows.reduce(
            (sum, row) =>
                sum + (Number(row.amount) || 0),
            0
        );
        const discount =
            subtotal *
            ((Number(labdisPer) || 0) / 100);
        setLabSubtotal(subtotal);
        setLabDisAmount(discount);
        setLabTotal(subtotal - discount);
    }, [labrows, labdisPer]);
// ============================================================
// RADIOLOGY TOTAL
// ============================================================
    useEffect(() => {
        const subtotal = invrows.reduce(
            (sum, row) =>
                sum + (Number(row.amount) || 0),
            0
        );
        const discount =
            subtotal *
            ((Number(invdisPer) || 0) / 100);
        setInvSubtotal(subtotal);
        setInvDisAmount(discount);
        setInvTotal(subtotal - discount);
    }, [invrows, invdisPer]);
// ============================================================
// DISCOUNT
// ============================================================
    const handleLabDiscountChange = event => {
        setLabDisPer(
            parseFloat(event.target.value) || 0
        );
    };
    const handleInvDiscountChange = event => {
        setInvDisPer(
            parseFloat(event.target.value) || 0
        );
    };
// ============================================================
// FORM DATA CHANGE
// ============================================================
    const handleLabChange = event => {
        const {
            name,
            value
        } = event.target;
        setLabData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    const handleInvChange = event => {
        const {
            name,
            value
        } = event.target;
        setInvData(prev => ({
            ...prev,
            [name]: value
        }));
    };
// ============================================================
// DEBUG
// ============================================================
    useEffect(() => {
        if (caseSheet) {
            console.log(
                "FRONTEND CASE SHEET:",
                JSON.stringify(
                    caseSheet,
                    null,
                    2
                )
            );
        }
    }, [caseSheet]);
    useEffect(() => {
        console.log(
            "FRONTEND LAB ROWS:",
            labrows
        );
    }, [labrows]);
    useEffect(() => {
        console.log(
            "FRONTEND RADIOLOGY ROWS:",
            invrows
        );
    }, [invrows]);
    useEffect(() => {
        console.log(
            "FRONTEND MEDICINE ROWS:",
            drugrows
        );
    }, [drugrows]);
// ============================================================
// VALIDATION
// ============================================================
    const hasValidLab = () => {
        return labrows.some(
            row =>
                row?.id &&
                Number(row?.amount) > 0
        );
    };
    const hasValidInv = () => {
        return invrows.some(
            row =>
                row?.id &&
                Number(row?.amount) > 0
        );
    };
    const hasValidDrug = () => {
        return drugrows.some(
            row =>
                row?.id &&
                getDrugName(row)
        );
    };
// ============================================================
// SUBMIT LAB
// ============================================================
    const formSubmitlab = async () => {
        const validRows =
            labrows.filter(
                row => row?.id
            );
        if (!validRows.length) {
            return null;
        }
        const res =
            await axios.get(
                `${baseUrl}get_appt_lab_number/`
            );
        const appt_lab =
            res.data.value;
        const fd = new FormData();
        fd.append(
            "appt_id",
            appt_lab
        );
        fd.append(
            "pat",
            "1"
        );
        fd.append(
            "cons",
            "1"
        );
        fd.append(
            "lab_id",
            appt_lab
        );
        fd.append(
            "ltest_id",
            validRows
                .map(row => row.id)
                .join(",")
        );
        fd.append(
            "lab_test",
            validRows
                .map(row => getLabName(row))
                .join(",")
        );
        fd.append(
            "lab_amount",
            validRows
                .map(row => row.amount || 0)
                .join(",")
        );
        fd.append(
            "lab_tot",
            labtotal || 0
        );
        fd.append(
            "lab_disc",
            labdisAmount || 0
        );
        fd.append(
            "lab_status",
            "1"
        );
        fd.append(
            "addedby",
            userName || ""
        );
        fd.append(
            "ip_add",
            ipAddress || ""
        );
        fd.append(
            "location",
            userLocation || ""
        );
        console.log(
            "SUBMITTING LAB:",
            validRows
        );
        return axios.post(
            `${baseUrl}lab-create/`,
            fd
        );
    };
// ============================================================
// SUBMIT RADIOLOGY
// ============================================================
    const formSubmitinv = async () => {
        const validRows =
            invrows.filter(
                row => row?.id
            );
        if (!validRows.length) {
            return null;
        }
        const res =
            await axios.get(
                `${baseUrl}get_appt_inv_number/`
            );
        const appt_inv =
            res.data.value;
        const fd = new FormData();
        fd.append(
            "appt_id",
            appt_inv
        );
        fd.append(
            "pat",
            "1"
        );
        fd.append(
            "cons",
            "1"
        );
        fd.append(
            "inv_id",
            "1"
        );
        fd.append(
            "itest_id",
            validRows
                .map(row => row.id)
                .join(",")
        );
        fd.append(
            "inv_test",
            validRows
                .map(row => getInvName(row))
                .join(",")
        );
        fd.append(
            "inv_amount",
            validRows
                .map(row => row.amount || 0)
                .join(",")
        );
        fd.append(
            "inv_tot",
            invtotal || 0
        );
        fd.append(
            "inv_disc",
            invdisAmount || 0
        );
        fd.append(
            "inv_status",
            "1"
        );
        fd.append(
            "addedby",
            userName || ""
        );
        fd.append(
            "ip_add",
            ipAddress || ""
        );
        fd.append(
            "location",
            userLocation || ""
        );
        console.log(
            "SUBMITTING RADIOLOGY:",
            validRows
        );
        return axios.post(
            `${baseUrl}inv-create/`,
            fd
        );
    };
// ============================================================
// SUBMIT MEDICINES
// ============================================================
    const submitHandler = async () => {
        const validRows =
            drugrows.filter(
                row =>
                    row?.id &&
                    getDrugName(row)
            );
        if (!validRows.length) {
            return null;
        }
        const fd = new FormData();
        fd.append(
            "illness",
            caseSheet?.chief_complaints || ""
        );
        fd.append(
            "complaints",
            caseSheet?.chief_complaints || ""
        );
        fd.append(
            "diagnosis",
            caseSheet?.diagnosis || ""
        );
        fd.append(
            "drug_status",
            "1"
        );
        fd.append(
            "drug_id",
            validRows
                .map(row => row.id)
                .join(",")
        );
        fd.append(
            "dose",
            validRows
                .map(row => row.dose || "")
                .join(",")
        );
        fd.append(
            "freq",
            validRows
                .map(row => row.freq || "")
                .join(",")
        );
        fd.append(
            "remarks",
            validRows
                .map(row => row.remarks || "")
                .join(",")
        );
        fd.append(
            "status",
            "1"
        );
        fd.append(
            "addedby",
            userName || ""
        );
        fd.append(
            "ip_add",
            ipAddress || ""
        );
        fd.append(
            "location",
            userLocation || ""
        );
        console.log(
            "SUBMITTING MEDICINES:",
            validRows
        );
        return axios.post(
            `${baseUrl}consultform/`,
            fd
        );
    };
// ============================================================
// FINAL SUBMIT
// ============================================================
    const handleFinalSubmit = async event => {
        event.preventDefault();
        if (!caseSheet) {
            Swal.fire(
                "Warning",
                "Case sheet is not available.",
                "warning"
            );
            return;
        }
        const validLab =
            hasValidLab();
        const validInv =
            hasValidInv();
        const validDrug =
            hasValidDrug();
        console.log(
            "FINAL CASE SHEET SUBMISSION"
        );
        console.log(
            "LAB:",
            validLab
        );
        console.log(
            "RADIOLOGY:",
            validInv
        );
        console.log(
            "MEDICINES:",
            validDrug
        );
        if (
            !validLab &&
            !validInv &&
            !validDrug
        ) {
            Swal.fire(
                "No Data",
                "There are no valid lab, radiology or medicine items to submit.",
                "warning"
            );
            return;
        }
        try {
            setLoading(true);
            const requests = [];
            if (validLab) {
                requests.push(
                    formSubmitlab()
                );
            }
            if (validInv) {
                requests.push(
                    formSubmitinv()
                );
            }
            if (validDrug) {
                requests.push(
                    submitHandler()
                );
            }
            await Promise.all(requests);
            setLoading(false);
            await Swal.fire({
                title:
                    "Case Sheet Submitted Successfully",
                text:
                    "The selected clinical orders have been saved.",
                icon:
                    "success",
                timer:
                    2500,
                showConfirmButton:
                    false
            });
            window.location.href =
                "/speechrx/";
        } catch (error) {
            console.error(
                "FINAL SUBMISSION ERROR:",
                error
            );
            setLoading(false);
            Swal.fire(
                "Error",
                "Case sheet submission failed.",
                "error"
            );
        }
    };
    return (<div
        className="max-w-xl mx-auto p-6 bg-white shadow-md rounded-xl mt-8"
    >

        {/* ====================================================
                HEADER
            ==================================================== */}

        <h3
            className="text-center"
            style={{
                color: "white", backgroundColor: "#0dcaf0", padding: "10px", borderRadius: "6px"
            }}
        >
            <i
                className="fa fa-microphone-lines"
                aria-hidden="true"
                style={{
                    color: "red", marginRight: "8px"
                }}
            />

            Case Sheet
        </h3>


        {/* ====================================================
                RECORDING CONTROLS
            ==================================================== */}

        <div className="mb-4">

            <div className="mb-3">

                <label className="form-label fw-bold">
                    Speech Language
                </label>

                <select
                    className="form-select"
                    value={language}
                    disabled={isRecording}
                    onChange={(e) => setLanguage(e.target.value)}
                    style={{
                        maxWidth: "250px"
                    }}
                >

                    <option value="ta-IN">
                        Tamil
                    </option>

                    <option value="en-IN">
                        English
                    </option>

                </select>

            </div>


            {/* =================================================
                    MAIN RECORD BUTTON
                ================================================= */}

            <div className="d-flex gap-3">

                <button
                    type="button"
                    className={isRecording ? "btn btn-danger" : "btn btn-success"}
                    onClick={isRecording ? sendConversation : startRecording}
                >

                    <i
                        className={isRecording ? "fa fa-stop" : "fa fa-microphone"}
                    />

                </button>


                <button
                    type="button"
                    onClick={startRecording}
                    disabled={isRecording}
                    className="btn btn-success"
                >

                    🎙 Start Consultation

                </button>


                <button
                    type="button"
                    onClick={sendConversation}
                    disabled={!isRecording}
                    className="btn btn-danger"
                >

                    ⏹ Finish Consultation

                </button>

            </div>


            {/* =================================================
                    RECORDING STATUS
                ================================================= */}

            {isRecording && (<div className="mt-3">

                <div className="alert alert-success">

                    🎙 Listening...

                    {speechListening && (<strong className="ms-2">

                        Microphone Active

                    </strong>)}

                </div>

            </div>)}


            {/* =================================================
                    LIVE TRANSCRIPT
                ================================================= */}

            {transcript && (<div className="mt-3">

                <label className="form-label fw-bold">

                    Live Conversation

                </label>

                <textarea
                    className="form-control"
                    rows={5}
                    value={transcript}
                    readOnly
                    style={{
                        background: "#F8FAFC", fontSize: "16px"
                    }}
                />

            </div>)}

        </div>


        {/* ====================================================
                RECORDING MESSAGE
            ==================================================== */}

        {isRecording && (<p
            className="text-orange-600 font-medium mb-3"
        >
            Recording in progress… 🎧
        </p>)}


        {/* ====================================================
                CONVERSATION ID
            ==================================================== */}

        {convId && (<p className="text-sm text-gray-700 mb-3">

            Conversation ID:

            <span className="font-semibold ms-1">

                        {convId}

                    </span>

        </p>)}


        {/* ====================================================
                PROCESSING
            ==================================================== */}

        {loading && (<div
            className="my-4 p-3 text-blue-700 bg-blue-100 rounded-lg text-center"
        >

            Processing audio… Please wait ⏳

        </div>)}


        {/* ====================================================
                CASE SHEET
            ==================================================== */}

        {caseSheet && (<div
            className="max-w-xl mx-auto mt-5 p-6 bg-white shadow-md rounded-xl"
        >

            {/* =================================================
                        CASE SHEET HEADER
                    ================================================= */}

            <h3
                className="text-center mb-4"
                style={{
                    color: "black",
                    backgroundColor: "#e5eaef",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "10px"
                }}
            >

                <i
                    className="fa fa-file-medical"
                    style={{
                        color: "red"
                    }}
                />

                Case Sheet

            </h3>


            {/* =================================================
                        CHIEF COMPLAINTS
                    ================================================= */}

            <div className="row">

                <div className="col-md-12 mb-3">

                    <label className="form-label fw-bold">

                        Chief Complaints

                    </label>

                    <textarea
                        className="form-control"
                        rows="2"
                        value={caseSheet.chief_complaints || ""}
                        readOnly
                    />

                </div>


                {/* =================================================
                            DIAGNOSIS
                        ================================================= */}

                <div className="col-md-12 mb-3">

                    <label className="form-label fw-bold">

                        Diagnosis

                    </label>

                    <textarea
                        className="form-control"
                        rows="2"
                        value={caseSheet.diagnosis || ""}
                        readOnly
                    />

                </div>


                {/* =================================================
                            ALLERGIES
                        ================================================= */}

                <div className="col-md-12 mb-3">

                    <label className="form-label fw-bold">

                        Allergies

                    </label>

                    <textarea
                        className="form-control"
                        rows="2"
                        value={caseSheet.allergies || ""}
                        readOnly
                    />

                </div>


                {/* =================================================
                            NOTES
                        ================================================= */}

                <div className="col-md-12 mb-3">

                    <label className="form-label fw-bold">

                        Notes

                    </label>

                    <textarea
                        className="form-control"
                        rows="2"
                        value={caseSheet.notes || ""}
                        readOnly
                    />

                </div>


                {/* =================================================
                            LAB SECTION
                        ================================================= */}

                <div className="col-md-12 mb-4">

                    <label className="form-label fw-bold">

                        Laboratory Investigations

                    </label>

                    <div className="table-responsive">

                        <table
                            className="erp-table-modern"
                            style={{
                                width: "100%"
                            }}
                        >

                            <thead>

                            <tr
                                style={{
                                    backgroundColor: "#1bc5ce", color: "white"
                                }}
                            >

                                <th>
                                    ID
                                </th>

                                <th>
                                    Lab Test Name
                                </th>

                                <th>
                                    Total
                                </th>

                                <th>
                                    Action
                                </th>

                            </tr>

                            </thead>


                            <tbody>

                            {labrows.map((row, index) => (<tr
                                key={index}
                            >

                                <td>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={row?.id || ""}
                                        readOnly
                                    />

                                </td>


                                <td>

                                    <AutoComplete
                                        options={labitems}

                                        value={row?.subcat || null}

                                        getOptionLabel={getLabName}

                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}

                                        onChange={(event, option) => {
                                            setLabRows(prev => {
                                                const updated = [...prev];
                                                updated[index] = {
                                                    ...updated[index],
                                                    id: option?.id || "",
                                                    subcat: option || "",
                                                    amount: option?.amount || 0
                                                };
                                                return updated;
                                            });
                                        }}

                                        renderInput={(params) => (<div
                                            ref={params.InputProps.ref}
                                        >

                                            <input
                                                className="form-control"
                                                type="text"
                                                {...params.inputProps}
                                                style={{
                                                    width: "100%", background: "#F7EEFF"
                                                }}
                                            />

                                        </div>)}

                                    />

                                </td>


                                <td>

                                    <input
                                        type="number"
                                        className="form-control"
                                        value={row?.amount || ""}
                                        readOnly
                                    />

                                </td>


                                <td>

                                    <button
                                        className="btn btn-danger"
                                        type="button"
                                        onClick={() => handleDeleteRowlab(index)}
                                    >

                                        Delete

                                    </button>

                                </td>

                            </tr>))}

                            </tbody>

                        </table>


                        <button
                            className="btn btn-success mt-3"
                            type="button"
                            onClick={handleAddRowlab}
                        >

                            Add More

                        </button>

                    </div>

                </div>


                {/* =================================================
                            RADIOLOGY SECTION
                        ================================================= */}

                <div className="col-md-12 mb-4">

                    <label className="form-label fw-bold">

                        Radiology / Investigation

                    </label>

                    <div className="table-responsive">

                        <table
                            className="erp-table-modern"
                            style={{
                                width: "100%"
                            }}
                        >

                            <thead>

                            <tr
                                style={{
                                    backgroundColor: "#1bc5ce", color: "white"
                                }}
                            >

                                <th>
                                    ID
                                </th>

                                <th>
                                    Radiology Test Name
                                </th>

                                <th>
                                    Total
                                </th>

                                <th>
                                    Action
                                </th>

                            </tr>

                            </thead>


                            <tbody>

                            {invrows.map((row, index) => (<tr
                                key={index}
                            >

                                <td>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={row?.id || ""}
                                        readOnly
                                    />

                                </td>


                                <td>

                                    <AutoComplete
                                        options={invitems}

                                        value={row?.components || null}

                                        getOptionLabel={getInvName}

                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}

                                        onChange={(event, option) => {
                                            setInvRows(prev => {
                                                const updated = [...prev];
                                                updated[index] = {
                                                    ...updated[index],
                                                    id: option?.id || "",
                                                    components: option || "",
                                                    amount: option?.amount || 0
                                                };
                                                return updated;
                                            });
                                        }}

                                        renderInput={(params) => (<div
                                            ref={params.InputProps.ref}
                                        >

                                            <input
                                                className="form-control"
                                                type="text"
                                                {...params.inputProps}
                                                style={{
                                                    width: "100%", background: "#F7EEFF"
                                                }}
                                            />

                                        </div>)}

                                    />

                                </td>


                                <td>

                                    <input
                                        type="number"
                                        className="form-control"
                                        value={row?.amount || ""}
                                        readOnly
                                    />

                                </td>


                                <td>

                                    <button
                                        className="btn btn-danger"
                                        type="button"
                                        onClick={() => handleDeleteRowinv(index)}
                                    >

                                        Delete

                                    </button>

                                </td>

                            </tr>))}

                            </tbody>

                        </table>


                        <button
                            className="btn btn-success mt-3"
                            type="button"
                            onClick={handleAddRowinv}
                        >

                            Add More

                        </button>

                    </div>

                </div>


                {/* =================================================
                            MEDICINE SECTION
                        ================================================= */}

                <div className="col-md-12 mb-4">

                    <label className="form-label fw-bold">

                        Prescription

                    </label>

                    <div className="table-responsive">

                        <table
                            className="erp-table-modern"
                            style={{
                                width: "100%"
                            }}
                        >

                            <thead>

                            <tr
                                style={{
                                    backgroundColor: "#1bc5ce", color: "white"
                                }}
                            >

                                <th>
                                    ID
                                </th>

                                <th>
                                    Drug Name
                                </th>

                                <th>
                                    Dose
                                </th>

                                <th>
                                    Intake
                                </th>

                                <th>
                                    Remarks
                                </th>

                                <th>
                                    Action
                                </th>

                            </tr>

                            </thead>


                            <tbody>

                            {drugrows.map((row, index) => (<tr
                                key={index}
                            >

                                <td>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={row?.id || ""}
                                        readOnly
                                    />

                                </td>


                                <td>

                                    <AutoComplete
                                        options={drugitems}

                                        value={row?.product || null}

                                        getOptionLabel={getDrugName}

                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}

                                        onChange={(event, option) => {
                                            setDrugRows(prev => {
                                                const updated = [...prev];
                                                updated[index] = {
                                                    ...updated[index],
                                                    id: option?.id || "",
                                                    product: option || null
                                                };
                                                return updated;
                                            });
                                        }}

                                        renderOption={(props, option) => (<Box
                                            component="li"
                                            {...props}
                                        >

                                            <div>

                                                <strong>
                                                    {getDrugName(option)}
                                                </strong>

                                                {option?.product?.expiry && (<div
                                                    style={{
                                                        fontSize: "12px"
                                                    }}
                                                >

                                                    Exp:
                                                    {" "}
                                                    {option.product.expiry}

                                                </div>)}

                                                {option?.product?.batch && (<div
                                                    style={{
                                                        fontSize: "12px"
                                                    }}
                                                >

                                                    Batch:
                                                    {" "}
                                                    {option.product.batch}

                                                </div>)}

                                                {option?.qty !== undefined && (<div
                                                    style={{
                                                        fontSize: "12px"
                                                    }}
                                                >

                                                    Qty:
                                                    {" "}
                                                    {option.qty}

                                                </div>)}

                                            </div>

                                        </Box>)}

                                        renderInput={(params) => (<div
                                            ref={params.InputProps.ref}
                                        >

                                            <input
                                                className="form-control"
                                                type="text"
                                                {...params.inputProps}
                                                style={{
                                                    width: "100%", background: "#F7EEFF"
                                                }}
                                            />

                                        </div>)}

                                    />

                                </td>


                                <td>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={row?.dose || ""}
                                        name="dose"
                                        onChange={e => handledrugChange(e, index)}
                                    />

                                </td>


                                <td>

                                    <select
                                        className="form-control"
                                        name="freq"
                                        value={row?.freq || ""}
                                        onChange={e => handledrugChange(e, index)}
                                    >

                                        <option value="">
                                            Select
                                        </option>

                                        <option value="After Food">
                                            After Food
                                        </option>

                                        <option value="Before Food">
                                            Before Food
                                        </option>

                                        <option value="30 Min Before Food">
                                            30 Min Before Food
                                        </option>

                                        <option value="15 Min Before Food">
                                            15 Min Before Food
                                        </option>

                                        <option value="Weekly Once">
                                            Weekly Once
                                        </option>

                                        <option value="Monthly Once">
                                            Monthly Once
                                        </option>

                                        <option value="HS">
                                            HS
                                        </option>

                                        <option value="HIS">
                                            HIS
                                        </option>

                                    </select>

                                </td>


                                <td>

                                    <select
                                        className="form-control"
                                        name="remarks"
                                        value={row?.remarks || ""}
                                        onChange={e => handledrugChange(e, index)}
                                    >

                                        <option value="">
                                            Empty
                                        </option>

                                        <option value="1-0-1">
                                            1-0-1
                                        </option>

                                        <option value="1-1-1">
                                            1-1-1
                                        </option>

                                        <option value="0-1-1">
                                            0-1-1
                                        </option>

                                        <option value="0-1-0">
                                            0-1-0
                                        </option>

                                        <option value="0-0-1">
                                            0-0-1
                                        </option>

                                        <option value="1-0-0">
                                            1-0-0
                                        </option>

                                        <option value="1-1-0">
                                            1-1-0
                                        </option>

                                    </select>

                                </td>


                                <td>

                                    <button
                                        className="btn btn-danger"
                                        type="button"
                                        onClick={() => handleDeleteRowdrug(index)}
                                    >

                                        Delete

                                    </button>

                                </td>

                            </tr>))}

                            </tbody>

                        </table>


                        <button
                            className="btn btn-success mt-3"
                            type="button"
                            onClick={handleAddRowdrug}
                        >

                            Add More

                        </button>

                    </div>

                </div>


                {/* =================================================
                            FINAL SUBMIT
                        ================================================= */}

                <div
                    className="col-md-12 text-center mt-4"
                >

                    <button
                        type="button"
                        className="btn btn-primary px-5"
                        onClick={handleFinalSubmit}
                        disabled={loading}
                    >

                        {loading ? "Submitting..." : "Submit Case Sheet"}

                    </button>

                </div>

            </div>

        </div>)}

    </div>);
}